import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, Loader2, RefreshCw, Send, Mail, Clock, 
  Users, Eye, ChevronRight, CheckCircle, XCircle,
  Sparkles, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterManager = () => {
  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(true);
  const [triggeringScheduler, setTriggeringScheduler] = useState(false);
  
  // Newsletter creation
  const [newsletterStep, setNewsletterStep] = useState('select');
  const [newsletterType, setNewsletterType] = useState('age');
  const [selectedAgeBracket, setSelectedAgeBracket] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  
  // Subscribers
  const [subscribers, setSubscribers] = useState([]);

  const AGE_BRACKETS = [
    { id: '0-5', label: '0-5 anni', icon: '👶', desc: 'Prima infanzia' },
    { id: '6-10', label: '6-10 anni', icon: '🧒', desc: 'Infanzia' },
    { id: '11-17', label: '11-17 anni', icon: '👦', desc: 'Adolescenza' },
    { id: '18-39', label: '18-39 anni', icon: '👨', desc: 'Giovani adulti' },
    { id: '40-59', label: '40-59 anni', icon: '🧔', desc: 'Adulti' },
    { id: '60+', label: '60+ anni', icon: '👴', desc: 'Terza età' }
  ];

  const THEMES = [
    { id: 'prevenzione', label: 'Prevenzione', icon: '🛡️', desc: 'Salute degli occhi' },
    { id: 'tecnologia', label: 'Tecnologia', icon: '💻', desc: 'Luce blu e schermi' },
    { id: 'stagionale', label: 'Stagionale', icon: '🌞', desc: 'Consigli del momento' },
    { id: 'novita', label: 'Novità', icon: '✨', desc: 'News dalla clinica' }
  ];

  useEffect(() => {
    loadSchedulerStatus();
    loadSubscribers();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      const res = await axios.get(`${API}/newsletter/scheduler-status`, getAuthHeaders());
      setSchedulerStatus(res.data);
    } catch (error) {
      console.error('Error loading scheduler:', error);
    } finally {
      setLoadingScheduler(false);
    }
  };

  const loadSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/newsletter/subscribers`, getAuthHeaders());
      setSubscribers(res.data.subscribers || []);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    }
  };

  const triggerDailyNewsletter = async () => {
    setTriggeringScheduler(true);
    try {
      await axios.post(`${API}/newsletter/trigger-daily`, {}, getAuthHeaders());
      toast.success('Newsletter giornaliera inviata!');
      loadSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio newsletter');
    } finally {
      setTriggeringScheduler(false);
    }
  };

  const generatePreview = async () => {
    if (newsletterType === 'age' && !selectedAgeBracket) {
      toast.error('Seleziona una fascia d\'età');
      return;
    }
    if (newsletterType === 'thematic' && !selectedTheme) {
      toast.error('Seleziona un tema');
      return;
    }

    setGeneratingPreview(true);
    try {
      const endpoint = newsletterType === 'age' 
        ? `${API}/newsletter-age/preview/${selectedAgeBracket}`
        : `${API}/newsletter/preview/thematic/${selectedTheme}`;
      
      const res = await axios.get(endpoint, getAuthHeaders());
      setNewsletterPreview(res.data);
      setNewsletterStep('preview');
    } catch (error) {
      toast.error('Errore generazione anteprima');
    } finally {
      setGeneratingPreview(false);
    }
  };

  const sendNewsletter = async (testOnly = false) => {
    setSendingNewsletter(true);
    try {
      const endpoint = newsletterType === 'age'
        ? `${API}/newsletter-age/send/${selectedAgeBracket}`
        : `${API}/newsletter/send/thematic/${selectedTheme}`;
      
      await axios.post(endpoint, { test_only: testOnly }, getAuthHeaders());
      toast.success(testOnly ? 'Email di test inviata!' : 'Newsletter inviata a tutti!');
      
      if (!testOnly) {
        setNewsletterStep('select');
        setNewsletterPreview(null);
        setSelectedAgeBracket(null);
        setSelectedTheme(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio');
    } finally {
      setSendingNewsletter(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Scheduler Automatico */}
      <div className="rounded-2xl p-6 shadow-sm border-2 border-green-200" style={{background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'}}>
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-800">
          <Calendar className="w-5 h-5" />
          📅 Invio Automatico Newsletter (Ogni Venerdì)
        </h3>
        
        {loadingScheduler ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : schedulerStatus ? (
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${schedulerStatus.scheduler_running ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                {schedulerStatus.scheduler_running ? '✓ Scheduler Attivo' : '✗ Scheduler Inattivo'}
              </span>
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                ⏰ Invio: {schedulerStatus.send_time || '08:00'}
              </span>
              <button
                onClick={loadSchedulerStatus}
                className="ml-auto p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-green-700" />
              </button>
            </div>
            
            {/* Countdown Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">🎂</span>
                  <span className="font-bold text-amber-800">Newsletter Giornaliera</span>
                </div>
                <div className="text-3xl font-bold text-amber-600 mb-1">{schedulerStatus.countdown_to_daily}</div>
                <p className="text-sm text-amber-700">Compleanni + Festività</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📰</span>
                  <span className="font-bold text-green-800">Newsletter Venerdì</span>
                </div>
                <div className="text-3xl font-bold text-green-600 mb-1">{schedulerStatus.countdown_to_friday}</div>
                <p className="text-sm text-green-700">
                  {schedulerStatus.next_friday_number}° venerdì → {schedulerStatus.next_friday_type}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3">
              <button
                onClick={triggerDailyNewsletter}
                disabled={triggeringScheduler}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                {triggeringScheduler ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Invia Newsletter Giornaliera Ora
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Impossibile caricare lo stato dello scheduler</p>
        )}
      </div>

      {/* Newsletter Manuale */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-[#2A9FCB]">
          <Mail className="w-5 h-5" />
          Crea Newsletter Manuale
        </h3>

        {newsletterStep === 'select' && (
          <div className="space-y-6">
            {/* Type Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => setNewsletterType('age')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  newsletterType === 'age' 
                    ? 'border-[#2A9FCB] bg-[#2A9FCB]/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-6 h-6 text-[#2A9FCB]" />
                  <span className="font-bold">Per Fascia d'Età</span>
                </div>
                <p className="text-sm text-gray-600">Contenuti personalizzati per età del paziente</p>
              </button>
              
              <button
                onClick={() => setNewsletterType('thematic')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  newsletterType === 'thematic' 
                    ? 'border-[#2A9FCB] bg-[#2A9FCB]/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-6 h-6 text-[#2A9FCB]" />
                  <span className="font-bold">Per Tema</span>
                </div>
                <p className="text-sm text-gray-600">Newsletter tematica per tutti</p>
              </button>
            </div>

            {/* Options */}
            {newsletterType === 'age' && (
              <div>
                <h4 className="font-medium mb-3">Seleziona fascia d'età:</h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {AGE_BRACKETS.map(bracket => (
                    <button
                      key={bracket.id}
                      onClick={() => setSelectedAgeBracket(bracket.id)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        selectedAgeBracket === bracket.id
                          ? 'border-[#2A9FCB] bg-[#2A9FCB]/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{bracket.icon}</div>
                      <div className="text-sm font-medium">{bracket.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {newsletterType === 'thematic' && (
              <div>
                <h4 className="font-medium mb-3">Seleziona tema:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {THEMES.map(theme => (
                    <button
                      key={theme.id}
                      onClick={() => setSelectedTheme(theme.id)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        selectedTheme === theme.id
                          ? 'border-[#2A9FCB] bg-[#2A9FCB]/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{theme.icon}</div>
                      <div className="font-medium">{theme.label}</div>
                      <div className="text-xs text-gray-500">{theme.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={generatePreview}
              disabled={generatingPreview || (newsletterType === 'age' && !selectedAgeBracket) || (newsletterType === 'thematic' && !selectedTheme)}
              className="w-full py-3 bg-[#2A9FCB] text-white rounded-xl font-medium hover:bg-[#2387b3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {generatingPreview ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5" />
                  Genera Anteprima
                </>
              )}
            </button>
          </div>
        )}

        {newsletterStep === 'preview' && newsletterPreview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-bold">Anteprima Newsletter</h4>
              <button
                onClick={() => { setNewsletterStep('select'); setNewsletterPreview(null); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Torna alla selezione
              </button>
            </div>
            
            <div className="border rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
              <div className="font-bold mb-2">{newsletterPreview.subject}</div>
              <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: newsletterPreview.html_preview || newsletterPreview.content }} />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => sendNewsletter(true)}
                disabled={sendingNewsletter}
                className="flex-1 py-2 border border-[#2A9FCB] text-[#2A9FCB] rounded-lg hover:bg-[#2A9FCB]/5 flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Invia Test a Me
              </button>
              <button
                onClick={() => sendNewsletter(false)}
                disabled={sendingNewsletter}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                {sendingNewsletter ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Invia a Tutti ({newsletterPreview.recipients_count || subscribers.length})
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Subscribers List */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2 text-[#2A9FCB]">
            <Users className="w-5 h-5" />
            Iscritti Newsletter ({subscribers.length})
          </h3>
          <button onClick={loadSubscribers} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        
        <div className="overflow-x-auto max-h-64">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left p-2 font-medium">Email</th>
                <th className="text-left p-2 font-medium">Nome</th>
                <th className="text-left p-2 font-medium">Iscritto il</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.slice(0, 50).map((sub, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="p-2">{sub.email}</td>
                  <td className="p-2">{sub.first_name} {sub.last_name}</td>
                  <td className="p-2">{sub.created_at ? new Date(sub.created_at).toLocaleDateString('it-IT') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsletterManager;
